
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface Teacher {
  id: string;
  label: string;
}

export const useTeacherSearch = (open: boolean, setOpen: (isOpen: boolean) => void) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch teachers when search query changes (debounced)
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 3) {
      setTeachers([]);
      return;
    }

    // If not open, open popover when user starts typing
    if (!open && searchQuery.length >= 3) {
      setOpen(true);
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      
      try {
        const { data, error } = await supabase.functions.invoke("search-teachers", {
          body: { query: searchQuery }
        });

        if (error) {
          console.error("Error fetching teachers:", error);
          toast({
            title: "Error",
            description: "Could not fetch teacher data. Please try again.",
            variant: "destructive",
          });
          setTeachers([]);
          return;
        }
        
        // Ensure we always have an array, even if empty
        const teacherResults = data?.teachers || [];
        setTeachers(teacherResults);
        
        // Make sure popover is open when results are available
        if (teacherResults.length > 0 && !open) {
          setOpen(true);
        }
      } catch (error) {
        console.error("Error in API request:", error);
        toast({
          title: "Error",
          description: "Connection error. Please check your network.",
          variant: "destructive",
        });
        // Always maintain array structure even on error
        setTeachers([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  return {
    searchQuery,
    setSearchQuery,
    teachers,
    isLoading
  };
};
